import { computed, reactive } from '@vue/composition-api';
import { Crawler } from 'es6-crawler-detect';
import { useRoute } from '@/composable/wrappers';
import { FeedService } from '@/services';
import { toggleURLParams } from '@/util';
import { ACTION_TITLES, ACTION_STATUS } from '@/constants';

const PAGE_MODE = {
  READ: 'read',
  WRITE: 'write',
};

const state = reactive({
  isPreview: false,
  pageMode: null,
  receiverId: '',
  loaded: false,
  counter: 0,
  lastAction: null,
  binaryFiles: [],
  action: {
    leads_uuid: '',
    verb: '',
    message: {
      visitor_id: '',
      contact_uuid: '',
      user_uuid: '',
      hashtagVisited: '',
      engagement_status: '',
      status: '',
      link: '',
      link_preview: '',
      data: {},
      source: 'web',
      text: '',
    },
    files: [],
  },
});

function setPageMode() {
  const route = useRoute();
  state.isPreview = route.value.query.preview === 'true' ? true : false;
  const isCrawler = new Crawler().isCrawler(navigator.userAgent);
  state.pageMode = state.isPreview || isCrawler ? PAGE_MODE.READ : PAGE_MODE.WRITE;
}

function updateAction() {
  const route = useRoute();
  const verb = route.value.name;

  state.action.verb = verb;
  state.action.leads_uuid = route.value.query.lead_id;
  state.receiverId = route.value.query.receiver_id || process.env.VUE_APP_UUID;

  state.action.message = {
    ...state.action.message,
    visitor_id: route.value.query.visitor_id || '',
    contact_uuid: route.value.query.contact_id || '',
    user_uuid: route.value.query.user_id || '',
    hashtagVisited: verb,
    text: ACTION_TITLES[verb],
  };
}

function updateActionLinks() {
  // Remove or append the preview param depending on the mode
  const url = window.location.href;
  const link = state.isPreview ? toggleURLParams(url, 'preview', 'true').href : url;
  const linkPreview = !state.isPreview ? toggleURLParams(url, 'preview', 'true').href : url;
  state.action.message.link = link;
  state.action.message.link_preview = linkPreview;
}

async function getLastActionMessage() {
  const { data } = await FeedService.getEngagementStatus(
    state.action.message.visitor_id,
    state.receiverId,
    state.action.verb
  );
  state.lastAction = data;
  state.action.message.engagement_status = data.engagement_status || data.status;
}

export default function useActionMessage() {
  // Check if the instance was created
  if (!state.loaded) {
    setPageMode();
    updateAction();
    updateActionLinks();
    getLastActionMessage();
    state.loaded = true;
  }

  const setData = (data) => {
    if (data) {
      state.action.message.data = { ...data };
    }
  };

  const setFiles = (files) => {
    state.binaryFiles = [...files];
  };

  const changeStatus = async (status) => {
    if (PAGE_MODE.READ === state.pageMode) {
      return;
    }

    if (state.binaryFiles.length) {
      const filesResponse = await FeedService.addEngagementFiles(
        state.action.message.visitor_id,
        state.action.leads_uuid,
        state.action.verb,
        state.binaryFiles
      );
      state.action.files = filesResponse
        .filter((promise) => promise.status === 'fulfilled')
        .map(({ value }) => ({
          filesystem_id: value.data[0].id,
          field_name: value.data[0].name,
        }));
    }
    // Update the status after the await so the status don't change if the request fail
    const { data } = await FeedService.sendMessageByReceiverId(state.receiverId, {
      ...state.action,
      ...{ message: { ...state.action.message, status } },
    });
    state.action.message.engagement_status = data.message.engagement_status;
    state.action.message.status = data.message.engagement_status;
  };

  const sentOpen = async () => {
    await changeStatus(ACTION_STATUS.OPENED);
  };

  const sentSubmitted = async () => {
    await changeStatus(ACTION_STATUS.SUBMITTED);
  };

  return {
    sentOpen,
    sentSubmitted,
    setData,
    setFiles,
    state,
    actionStatus: computed(() => {
      return state.action.message.engagement_status || state.action.message.status;
    }),
    actionOwner: computed(() => {
      return state.lastAction?.owner;
    }),
  };
}
